import { relativeTime, type AppNotification, type NotificationKind } from "@love-cpmd/core";
import { cn } from "@love-cpmd/ui";
import { AlarmClock, ArrowLeftRight, BellOff, CalendarClock, Megaphone, NotebookText, RotateCw, type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useAction } from "../../lib/queries";

const KIND_ICON: Record<NotificationKind, { Icon: LucideIcon; className: string }> = {
  reminder_h7: { Icon: CalendarClock, className: "bg-scheduled-soft text-scheduled" },
  reminder_h1: { Icon: AlarmClock, className: "bg-primary-fixed text-primary" },
  dday: { Icon: Megaphone, className: "bg-primary-container text-on-primary" },
  schedule_changed: { Icon: RotateCw, className: "bg-rescheduled-soft text-rescheduled" },
  schedule_swapped: { Icon: ArrowLeftRight, className: "bg-swap-soft text-swap" },
  summary_published: { Icon: NotebookText, className: "bg-completed-soft text-completed" },
};

/** F9: tapping marks it read, then opens the related session (or summary). */
export function useOpenNotification() {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const markRead = useAction(api.markNotificationRead);
  return (n: AppNotification) => {
    if (userId && !n.readAt) markRead.fire(userId, n.id);
    if (n.kind === "summary_published" && n.sessionId) navigate(`/summary/${n.sessionId}`);
    else if (n.sessionId) navigate(`/schedule/calendar?session=${n.sessionId}`);
  };
}

export function NotificationList({ items, compact, onItemClick }: { items: AppNotification[]; compact?: boolean; onItemClick?: () => void }) {
  const open = useOpenNotification();
  if (!items.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
        <div className="flex size-11 items-center justify-center rounded-2xl bg-surface-container text-on-surface-variant">
          <BellOff className="size-5" aria-hidden />
        </div>
        <p className="text-label-lg text-on-surface">You're all caught up</p>
        <p className="text-body-sm text-on-surface-variant">Reminders and schedule changes will show up here.</p>
      </div>
    );
  }
  return (
    <ul className="flex flex-col">
      {items.map((n) => {
        const meta = KIND_ICON[n.kind];
        return (
          <li key={n.id}>
            <button
              type="button"
              onClick={() => {
                open(n);
                onItemClick?.();
              }}
              className={cn(
                "flex w-full items-start gap-3 text-left transition-colors hover:bg-surface-container-low",
                compact ? "rounded-xl px-3 py-2.5" : "rounded-2xl px-4 py-3.5",
                !n.readAt && "bg-primary-soft/70",
              )}
            >
              <span className={cn("mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl", meta.className)}>
                <meta.Icon className="size-[18px]" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-start justify-between gap-2">
                  <span className={cn("text-label-lg text-on-surface", !n.readAt && "font-bold")}>{n.title}</span>
                  {!n.readAt && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary-container" aria-label="Unread" />}
                </span>
                <span className="mt-0.5 block text-body-sm text-on-surface-variant">{n.body}</span>
                <span className="mt-1 block text-label-sm text-outline">{relativeTime(n.createdAt)}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
